import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";

import { bindActionCreators, Dispatch } from "redux";

import { FilterSection, FilterItem, FilterItemField, FilterTextInput, Dropdown, PickerInput, RequiredState, DataColumnDef, InlineOverlay } from "@plex/react-components";

import { arrayHelpers } from "infrastructure/helpers";
import { ILocalizable, ILocale, isDefaultLocale, commonMessages } from "infrastructure/localization";
import { ISettingsState } from "ui/app";
import { useLocalizedValues, useLocalizer, useLocalizedDefaultLocale } from "ui/hooks";
import { glossaryMessages } from "ui/screens/plex-dictionary/plex-dictionary.localization";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";
import { IIndustry } from "ui/shared/models/common";

import { translationsManagerActions } from "../TranslationsManager.actions";
import { ITranslationsManagerState, ITranslationsManagerFilterData } from "../TranslationsManager.state";

interface IFilterProps {
  industries: IIndustry[];
  industryCanOpen?: boolean;
  locales: ILocale[];
  supportedLocales?: ILocale[];
  currentLocaleIdentifier: string;
  localeCanOpen?: boolean;
  statuses: ILocalizable[];
  sources: ILocalizable[];
  applyFilter: (data: ITranslationsManagerFilterData) => void;
  search: () => void;
  loadIndustries: (criterion: IPickerSearchCriterion<IIndustry>) => void;
  loadLocales: (criterion: IPickerSearchCriterion<ILocale>) => void;
  loadSupportedLocales: () => void;
}

export const TranslationsManagerFilter: React.FC<IFilterProps> = ({
  industries,
  industryCanOpen,
  statuses,
  locales,
  supportedLocales,
  currentLocaleIdentifier,
  localeCanOpen,
  sources,
  search,
  applyFilter,
  loadIndustries,
  loadLocales,
  loadSupportedLocales
}) => {
  const localize = useLocalizer();
  const defaultLocale = useLocalizedDefaultLocale();

  const localizedValues = useLocalizedValues(() => {
    return {
      locale: glossaryMessages.filter("locale"),
      status: glossaryMessages.filter("status"),
      industry: glossaryMessages.filter("industry"),
      text: glossaryMessages.filter("text"),
      provider: glossaryMessages.filter("provider"),
      localeIdentifier: commonMessages.property("localeIdentifier"),
      name: commonMessages.property("localeName"),
      code: glossaryMessages.property("code"),
      description: glossaryMessages.property("description"),
      all: commonMessages.property("all")
    };
  });

  const [state, setState] = useState<ITranslationsManagerFilterData & { localePickerInitialized: boolean }>({ localePickerInitialized: false });
  const [pinned, setPinned] = useState(false);

  const onFilterChange = <T, P extends keyof ITranslationsManagerFilterData>(value: T, prop: P) => {
    setState({ ...state, [prop]: value });
  };

  const onFilterSelect = <T, P extends keyof ITranslationsManagerFilterData>(items: T[], prop: P) => {
    setState({ ...state, [prop]: items && items.length ? items[0] : undefined });
  };

  const selectedLocales = useMemo(() => {
    return arrayHelpers.createFrom(state.selectedLocale);
  }, [state.selectedLocale]);

  const selectedIndustries = useMemo(() => {
    return arrayHelpers.createFrom(state.selectedIndustry);
  }, [state.selectedIndustry]);

  useEffect(() => {
    loadSupportedLocales();
  }, [loadSupportedLocales]);

  useEffect(() => {
    if (supportedLocales && !state.localePickerInitialized) {
      setState(prevState => ({
        ...prevState,
        selectedLocale: supportedLocales.filter(e => e.localeIdentifier === currentLocaleIdentifier)[0] || defaultLocale,
        localePickerInitialized: true
      }));
    }
  }, [supportedLocales, currentLocaleIdentifier, state.localePickerInitialized, defaultLocale]);

  return (
    <FilterSection
      pinned={pinned}
      onSearch={() => {
        applyFilter(state);
        search();
      }}
      onPin={setPinned}
    >
      <FilterItem labelText={localizedValues.locale} required={RequiredState.required}>
        <FilterItemField shouldCollapse={() => !state.selectedLocale} value={state.selectedLocale}>
          <InlineOverlay show={!state.localePickerInitialized} spinnerPosition={"left"} showSpinner>
            <PickerInput
              autoPick
              dialogTitle={localizedValues.locale}
              data={locales}
              isOpen={localeCanOpen}
              keySelector={row => (row ? row.localeIdentifier : "")}
              displaySelector={row => (row ? row.name : "")}
              selected={selectedLocales}
              onSelectionChanged={rows => onFilterSelect(rows, "selectedLocale")}
              onSearch={(q, cols) => loadLocales({ query: q, searchedColumns: cols })}
              searchText=""
            >
              <DataColumnDef<ILocale> id="localeIdentifier" title={localizedValues.localeIdentifier} valueSelector={row => row.localeIdentifier} searchable sortable>
                {row => row.localeIdentifier}
              </DataColumnDef>
              <DataColumnDef<ILocale> id="name" title={localizedValues.name} valueSelector={row => row.name} searchable sortable>
                {row => row.name}
              </DataColumnDef>
            </PickerInput>
          </InlineOverlay>
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.status}>
        <FilterItemField shouldCollapse={() => !state.selectedStatus} value={state.selectedStatus}>
          <Dropdown
            id="status"
            items={statuses}
            includeEmpty
            placeholder={localizedValues.all}
            keySelector={item => (item as ILocalizable).messageId.value}
            displaySelector={item => localize((item as ILocalizable).messageId)}
            selected={arrayHelpers.createFrom(state.selectedStatus)}
            onSelectionChanged={items => onFilterSelect(items, "selectedStatus")}
          />
        </FilterItemField>
      </FilterItem>
      <FilterItem
        labelText={localizedValues.industry}
        required={isDefaultLocale(state.selectedLocale && state.selectedLocale.localeIdentifier) ? RequiredState.required : undefined}
      >
        <FilterItemField shouldCollapse={() => !state.selectedIndustry}>
          <PickerInput
            autoPick
            dialogTitle={localizedValues.industry}
            data={industries}
            isOpen={industryCanOpen}
            keySelector={row => row.isiCv4Code}
            displaySelector={row => row.description}
            selected={selectedIndustries}
            onSelectionChanged={rows => onFilterSelect(rows, "selectedIndustry")}
            onSearch={(q, cols) => loadIndustries({ query: q, searchedColumns: cols })}
            searchText=""
          >
            <DataColumnDef<IIndustry> id="isiCv4Code" title={localizedValues.code} valueSelector={row => row.isiCv4Code} searchable>
              {row => row.isiCv4Code}
            </DataColumnDef>
            <DataColumnDef<IIndustry> id="description" title={localizedValues.description} valueSelector={row => row.description} searchable>
              {row => row.description}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.text}>
        <FilterItemField shouldCollapse={() => !state.filterText}>
          <FilterTextInput id="text" value={state.filterText || ""} onChange={e => onFilterChange(e.currentTarget.value, "filterText")} />
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.provider}>
        <FilterItemField shouldCollapse={() => !state.selectedSource}>
          <Dropdown
            id="provider"
            items={sources}
            includeEmpty
            placeholder={localizedValues.all}
            keySelector={item => (item as ILocalizable).messageId.value}
            displaySelector={item => localize((item as ILocalizable).messageId)}
            selected={arrayHelpers.createFrom(state.selectedSource)}
            onSelectionChanged={items => onFilterSelect(items, "selectedSource")}
          />
        </FilterItemField>
      </FilterItem>
    </FilterSection>
  );
};

export default connect(
  (state: { translationsManager: ITranslationsManagerState; settings: ISettingsState }) => {
    const filter = state.translationsManager.filter;
    return {
      locales: filter.locales,
      industries: filter.industries,
      statuses: filter.statuses,
      sources: filter.sources,
      industryCanOpen: filter.industryCanOpen,
      localeCanOpen: filter.localeCanOpen,
      supportedLocales: filter.supportedLocales,
      currentLocaleIdentifier: state.settings.localSettings.locale
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        applyFilter: translationsManagerActions.applyFilter,
        search: translationsManagerActions.search,
        loadIndustries: translationsManagerActions.loadIndustries,
        loadLocales: translationsManagerActions.loadLocales,
        loadSupportedLocales: translationsManagerActions.loadSupportedLocales
      },
      dispatch
    );
  }
)(TranslationsManagerFilter);
